import React, { useState } from "react";
import { Loader2, FileDown, FileCode, Send } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { INVOICE_STATUS_META, INVOICE_STATUSES, fmtEUR, isNoVat, vatMention } from "../../lib/invoiceConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import ReminderPanel from "./ReminderPanel";

const INVOICE_REMINDER_TYPES = [
  { value: "invoice_due_soon", label: "Facture bientôt échue" },
  { value: "invoice_overdue", label: "Facture impayée" },
];

const InvoiceStatusBadge = ({ status }) => {
  const m = INVOICE_STATUS_META[status] || { label: status, color: "bg-gray-100 text-gray-600" };
  return <Badge variant="outline" className={`${m.color} font-medium`} data-testid={`invoice-status-${status}`}>{m.label}</Badge>;
};

const InvoiceDetail = ({ open, onOpenChange, invoice, role, onUpdated }) => {
  const [statusValue, setStatusValue] = useState("");
  const [platformRef, setPlatformRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("virement");
  const [payLoading, setPayLoading] = useState(false);

  if (!invoice) return null;
  const isAdmin = role === "admin";
  const seller = invoice.seller || {};
  const noVat = isNoVat(seller.vatRegime);
  const remaining = Math.round(((invoice.totalTTC || 0) - (invoice.paidAmount || 0)) * 100) / 100;

  const recordPayment = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide."); return; }
    setPayLoading(true);
    try {
      const { data } = await API.patch(`/invoices/${invoice.id}/payment`, { amount: amt, mode: payMode });
      toast.success("Paiement enregistré");
      setPayAmount("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPayLoading(false);
    }
  };

  const refreshDoc = async () => {
    try {
      const { data } = await API.get(`/invoices/${invoice.id}`);
      onUpdated?.(data);
    } catch {
      /* ignore */
    }
  };

  const downloadBlob = async (url, filename, openInline = false) => {
    try {
      const res = await API.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(res.data);
      if (openInline) {
        window.open(blobUrl, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      toast.error("Téléchargement impossible.");
    }
  };

  const applyStatus = async () => {
    if (!statusValue) return;
    setLoading(true);
    try {
      const { data } = await API.patch(`/invoices/${invoice.id}/status`, {
        status: statusValue,
        platformReference: platformRef || null,
      });
      toast.success("Statut facture mis à jour");
      setStatusValue("");
      setPlatformRef("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[hsl(207,78%,42%)]">{invoice.number || "Brouillon"}</span>
            <InvoiceStatusBadge status={invoice.status} />
          </DialogTitle>
          <DialogDescription>Détail de la facture, cycle de vie e-invoicing, export PDF et données structurées.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Parties */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-slate-800 mb-1">Vendeur</p>
              <p>{seller.name}</p>
              {seller.address && <p className="text-muted-foreground">{seller.address}, {seller.postalCode} {seller.city}</p>}
              {seller.siret && <p className="text-muted-foreground text-xs">SIRET : {seller.siret}</p>}
              {seller.tvaIntra && <p className="text-muted-foreground text-xs">TVA : {seller.tvaIntra}</p>}
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Client</p>
              <p>{invoice.buyer?.name}</p>
              {invoice.buyer?.address && <p className="text-muted-foreground">{invoice.buyer.address}, {invoice.buyer.postalCode} {invoice.buyer.city}</p>}
              {invoice.buyer?.siren && <p className="text-muted-foreground text-xs">SIREN : {invoice.buyer.siren}</p>}
              {invoice.buyer?.tvaIntra && <p className="text-muted-foreground text-xs">TVA : {invoice.buyer.tvaIntra}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><span className="text-muted-foreground">Émission</span><br />{invoice.issueDate}</div>
            <div><span className="text-muted-foreground">Prestation</span><br />{invoice.saleDate || "-"}</div>
            <div><span className="text-muted-foreground">Échéance</span><br />{invoice.dueDate || "-"}</div>
            {invoice.interventionDossier && <div><span className="text-muted-foreground">Dossier</span><br />{invoice.interventionDossier}</div>}
          </div>

          <Separator />

          {/* Lignes */}
          <div className="space-y-1">
            {invoice.lines?.map((l, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{l.designation} <span className="text-muted-foreground">×{l.quantity} @ {fmtEUR(l.unitPriceHT)}{!noVat ? ` (TVA ${l.vatRate}%)` : ""}</span></span>
                <span className="font-medium">{fmtEUR(l.totalHT)}</span>
              </div>
            ))}
          </div>

          <div className="bg-slate-50 rounded p-3 ml-auto sm:w-72 text-sm space-y-1">
            <div className="flex justify-between"><span>Total HT</span><span>{fmtEUR(invoice.totalHT)}</span></div>
            {!noVat && invoice.vatBreakdown?.map((b) => (
              <div key={b.rate} className="flex justify-between text-xs text-muted-foreground"><span>TVA {b.rate}%</span><span>{fmtEUR(b.vatAmount)}</span></div>
            ))}
            <div className="flex justify-between font-bold text-[hsl(207,78%,42%)] border-t pt-1"><span>{noVat ? "Net à payer" : "Total TTC"}</span><span>{fmtEUR(noVat ? invoice.totalHT : invoice.totalTTC)}</span></div>
            {invoice.paidAmount > 0 && (
              <>
                <div className="flex justify-between text-xs text-emerald-700"><span>Réglé</span><span>{fmtEUR(invoice.paidAmount)}</span></div>
                {remaining > 0 && <div className="flex justify-between text-xs text-orange-700 font-medium"><span>Reste à payer</span><span>{fmtEUR(remaining)}</span></div>}
              </>
            )}
            {noVat && vatMention(seller.vatRegime) && <p className="text-[11px] text-muted-foreground pt-1">{vatMention(seller.vatRegime)}</p>}
          </div>

          {invoice.platformReference && (
            <p className="text-xs text-muted-foreground">Réf. plateforme : {invoice.platformReference}</p>
          )}

          {/* Téléchargements */}
          <Separator />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadBlob(`/invoices/${invoice.id}/pdf`, "facture.pdf", true)} data-testid="invoice-pdf-button">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadBlob(`/invoices/${invoice.id}/structured?format=xml`, `${invoice.number || "facture"}.xml`)} data-testid="invoice-xml-button">
              <FileCode className="w-4 h-4 mr-2" /> Export structuré (CII)
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            L'export structuré (Factur-X/UBL/CII) est une <strong>préparation non certifiée</strong>. La transmission B2B officielle devra passer par une plateforme agréée (PDP).
          </p>
          {isAdmin && invoice.clientDownloadedAt && (
            <p className="text-xs text-emerald-700">✓ Consultée par le client le {(invoice.clientDownloadedAt || "").slice(0, 16).replace("T", " ")}</p>
          )}

          {/* Actions admin : statut e-invoicing */}
          {isAdmin && (
            <>
              <Separator />
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <p className="font-semibold text-sm text-slate-800">Cycle de vie de la facture</p>
                <div className="space-y-2">
                  <Label className="text-xs">Réf. plateforme agréée (si transmise)</Label>
                  <Input value={platformRef} onChange={(e) => setPlatformRef(e.target.value)} placeholder="Identifiant retourné par la PDP" data-testid="invoice-platform-ref" />
                </div>
                <div className="flex gap-2">
                  <Select value={statusValue} onValueChange={setStatusValue}>
                    <SelectTrigger data-testid="invoice-status-select"><SelectValue placeholder="Nouveau statut" /></SelectTrigger>
                    <SelectContent>
                      {INVOICE_STATUSES.map((s) => (<SelectItem key={s} value={s}>{INVOICE_STATUS_META[s].label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Button disabled={!statusValue || loading} onClick={applyStatus} data-testid="invoice-apply-status">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Appliquer
                  </Button>
                </div>
                {invoice.status === "brouillon" && (
                  <p className="text-[11px] text-amber-700">Passer à « Envoyée » attribue le numéro légal définitif et fige les informations vendeur.</p>
                )}
              </div>

              {invoice.number && invoice.status !== "payee" && (
                <div className="bg-emerald-50 rounded-lg p-4 space-y-2 mt-3">
                  <p className="font-semibold text-sm text-slate-800">Enregistrer un paiement (partiel / acompte / solde)</p>
                  <div className="flex gap-2 flex-wrap items-end">
                    <div className="space-y-1">
                      <Label className="text-xs">Montant reçu (€)</Label>
                      <Input type="number" min="0" step="0.01" className="w-32" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder={remaining > 0 ? String(remaining) : ""} data-testid="payment-amount-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mode</Label>
                      <Select value={payMode} onValueChange={setPayMode}>
                        <SelectTrigger className="w-36" data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="virement">Virement</SelectItem>
                          <SelectItem value="cheque">Chèque</SelectItem>
                          <SelectItem value="especes">Espèces</SelectItem>
                          <SelectItem value="cb">Carte</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button disabled={payLoading} onClick={recordPayment} data-testid="record-payment-button">
                      {payLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {isAdmin && ["envoyee", "transmise_plateforme", "acceptee"].includes(invoice.status) && (
            <ReminderPanel docType="invoice" doc={invoice} types={INVOICE_REMINDER_TYPES} onSent={refreshDoc} />
          )}

          {/* Historique */}
          <Separator />
          <div>
            <p className="font-semibold text-sm text-slate-800 mb-2">Historique</p>
            <div className="space-y-1">
              {(invoice.statusHistory || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  <InvoiceStatusBadge status={h.status} />
                  {h.note && <span className="text-slate-600">— {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDetail;
